/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component } from 'react'
import { cloneDeep } from 'lodash'

import type { EuiComboBoxOptionOption } from '@elastic/eui'
import { EuiComboBox, EuiSpacer, EuiText } from '@elastic/eui'

import type {
  ElasticsearchClusterTopologyElement,
  InstanceConfiguration,
} from '@modules/cloud-api/v1/types'
import type { AnyTopologyElement } from '@modules/ui-types'

import { getNodeTypeForNodeRoles } from '@/lib/deploymentTemplates/deploymentTemplate'
import { getNodeRoles } from '@/lib/stackDeployments/selectors/nodeRoles'
import { getAutoscalingMaxForInstanceTemplate } from '@/lib/stackDeployments/selectors/autoscaling'

import type { WrappedComponentProps } from 'react-intl'
import type { ReactNode } from 'react'

export interface Props extends WrappedComponentProps {
  emptyPrompt?: ReactNode
  topologyElement: AnyTopologyElement
  instanceConfiguration: InstanceConfiguration
  instanceConfigurations: InstanceConfiguration[]
  onChange: ((path: string[], value: any) => void) | undefined
}

type State = {
  isTitlePopoverOpen: boolean
  isDeprecatedPopoverOpen: boolean
}

class SelectInstanceConfiguration extends Component<Props, State> {
  state = {
    isTitlePopoverOpen: false,
    isDeprecatedPopoverOpen: false,
  }

  render() {
    const { instanceConfiguration, instanceConfigurations, onChange } = this.props

    const empty = { label: ``, value: `` }

    const options: Array<EuiComboBoxOptionOption<string>> = instanceConfigurations.map(
      ({ id, name, description = `` }) => ({
        value: id || name,
        label: name,
        description,
      }),
    )
    const selectedValue = options.filter((option) => option.value === instanceConfiguration.id)

    if (!selectedValue) {
      options.unshift(empty)
    }

    return (
      <EuiComboBox
        fullWidth={true}
        style={{ width: `500px` }}
        isClearable={false}
        singleSelection={{ asPlainText: true }}
        isDisabled={!onChange}
        onChange={([selectedOption]) => this.onSelectInstanceConfiguration(selectedOption?.value)}
        selectedOptions={selectedValue}
        options={options}
        renderOption={(option) => this.renderOption(option)}
        rowHeight={75}
        data-test-id='instance-configuration-select'
      />
    )
  }

  renderOption(option: any) {
    return (
      <div>
        <EuiText data-test-id='instance-config-label' size='s'>
          <strong>{option.label}</strong>
        </EuiText>

        <EuiSpacer size='xs' />

        <EuiText
          style={{
            maxHeight: '3rem',
            overflow: 'hidden',
            whiteSpace: 'normal',
            display: 'block',
          }}
          size='s'
          color='subdued'
        >
          {option.description}
        </EuiText>
      </div>
    )
  }

  onSelectInstanceConfiguration = (id: string | undefined) => {
    const { onChange, instanceConfigurations } = this.props

    if (!onChange) {
      return // read-only
    }

    if (!id) {
      return // sanity
    }

    const instanceConfiguration = instanceConfigurations.find((option) => option.id === id)

    if (instanceConfiguration == null) {
      return // sanity
    }

    const topologyElement = cloneDeep(this.props.topologyElement)

    const nodeRoles = getNodeRoles({ topologyElement: this.props.topologyElement })
    const resource = instanceConfiguration.discrete_sizes.resource || `memory`
    const defaultSize = instanceConfiguration.discrete_sizes.default_size || 0

    topologyElement.instance_configuration_id = id
    topologyElement.size = { value: defaultSize, resource }
    topologyElement.zone_count = instanceConfiguration.max_zones || 1

    if (instanceConfiguration.instance_type === `elasticsearch`) {
      const esTopologyElement = topologyElement as ElasticsearchClusterTopologyElement
      const originalEsTopology = this.props.topologyElement as ElasticsearchClusterTopologyElement

      // Backend validation (AutoscalingConfigValidation.scala):
      // - ML: MUST have both autoscaling_min and autoscaling_max
      // - Data (hot/warm/cold): MUST have autoscaling_max, NO autoscaling_min
      // - data_frozen: does not need to have autoscaling_min or autoscaling_max
      // - Master/Ingest/Coordinator: MUST have NEITHER

      const isML = nodeRoles.includes('ml')
      const isDataTier = nodeRoles.some((role) =>
        ['data_hot', 'data_content', 'data_warm', 'data_cold', 'data_frozen'].includes(role),
      )

      if (isML) {
        esTopologyElement.autoscaling_max = getAutoscalingMaxForInstanceTemplate({
          instanceConfiguration,
          resource,
        })
        esTopologyElement.autoscaling_min = originalEsTopology.autoscaling_min || {
          value: defaultSize,
          resource,
        }
      } else if (isDataTier) {
        esTopologyElement.autoscaling_max = getAutoscalingMaxForInstanceTemplate({
          instanceConfiguration,
          resource,
        })
      }
    }

    const nodeType = getNodeTypeForNodeRoles({
      nodeRoles,
      instanceConfiguration,
    })

    if (nodeType) {
      const esTopologyElement = topologyElement as ElasticsearchClusterTopologyElement
      esTopologyElement.node_type = nodeType
    }

    // make atomic change
    onChange([], topologyElement)
  }
}

export default SelectInstanceConfiguration
